/* Colour-aware photos and films share one crossfade gallery and connected enlarged view. */
if (!customElements.get('yv-colour-gallery')) {
  customElements.define('yv-colour-gallery', class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.photos = [...this.querySelectorAll('[data-yv-photo]')];
      // Keep the cover photo; surface the first film immediately after it.
      const film = this.photos.find(photo => photo.hasAttribute('data-yv-video'));
      if (film && this.photos[0] !== film) {
        this.photos[0].after(film);
        this.photos = [...this.querySelector('.yv-colour-gallery__stage').children];
      }
      this.dialog = this.querySelector('dialog');
      this.currentColour = null;
      this.thumbnailRail = this.querySelector('.yv-colour-gallery__thumbnails');
      this.thumbnails = this.photos.map((photo, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.yvThumbnail = index;
        button.setAttribute('aria-label', `Show ${photo.dataset.label}`);
        const image = document.createElement('img'), original = photo.querySelector('img');
        image.src = original.dataset.thumbnail || original.src;
        image.alt = ''; image.width = 80; image.height = 100; image.loading = 'lazy'; image.draggable = false;
        button.append(image);
        if (photo.hasAttribute('data-yv-video')) {
          const badge = document.createElement('span');
          badge.className = 'yv-video-thumb-badge'; badge.textContent = '▶'; badge.setAttribute('aria-hidden', 'true');
          button.append(badge);
          button.setAttribute('aria-label', `Show video: ${photo.dataset.label}`);
        }
        button.addEventListener('click', () => this.show(this.visiblePhotos.indexOf(photo)), options);
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const current = this.visiblePhotos.indexOf(photo);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.visiblePhotos.length - 1
            : Math.max(0, Math.min(this.visiblePhotos.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
          this.thumbnails[this.photos.indexOf(this.visiblePhotos[next])].focus({preventScroll: true});
          this.show(next);
        }, options);
        return button;
      });
      this.thumbIndicator = document.createElement('span');
      this.thumbIndicator.className = 'yv-thumb-indicator';
      this.thumbIndicator.setAttribute('aria-hidden', 'true');
      this.thumbnailRail.replaceChildren(...this.thumbnails, this.thumbIndicator);
      this.thumbnailRail.dataset.indicatorReady = 'true';
      this.thumbResize = new ResizeObserver(() => this.positionThumbnail());
      this.thumbResize.observe(this.thumbnailRail);
      window.YVDragScroll(this.thumbnailRail, this.events.signal);
      this.previous = this.querySelector('[data-yv-previous]');
      this.next = this.querySelector('[data-yv-next]');
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.slider = new window.YVSwipeRail(this.querySelector('.yv-colour-gallery__stage'), {
        items: [], onChange: index => this.update(index)
      });
      this.photos.filter(photo => !photo.hasAttribute('data-yv-video')).forEach(photo => photo.querySelector('button').addEventListener('click', () => {
        this.show(this.visiblePhotos.indexOf(photo), false);
        this.openZoom();
      }, options));
      this.photos.filter(photo => photo.hasAttribute('data-yv-video')).forEach(photo => this.bindVideo(photo, this.events.signal));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
      }, options);
      this.mediaObserver = new IntersectionObserver(entries => {
        if (!this.dialog.open && !entries[0].isIntersecting) this.pauseVideos();
      });
      this.mediaObserver.observe(this);
      this.querySelector('[data-yv-close]').addEventListener('click', () => this.closeZoom(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeZoom(); }, options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.closeZoom(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.target.closest('video, iframe, input, select, textarea')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.zoomSlider?.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.querySelector('[data-yv-zoom-previous]').addEventListener('click', () => this.zoomSlider?.move(-1), options);
      this.querySelector('[data-yv-zoom-next]').addEventListener('click', () => this.zoomSlider?.move(1), options);
      this.dialog.addEventListener('close', () => {
        this.pauseVideos(); this.zoomEvents?.abort();
        this.dialog.getAnimations({subtree: true}).forEach(animation => animation.cancel());
        if (this.rootOverflow !== undefined) document.documentElement.style.overflow = this.rootOverflow;
        this.rootOverflow = undefined; this.closing = false;
        this.zoomSlider?.destroy(); this.zoomSlider = null;
        this.visiblePhotos[this.activeIndex]?.querySelector('button').focus({preventScroll: true});
      }, options);
      this.setColour(this.dataset.colour);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data.sectionId) !== this.dataset.section) return;
          const colour = event.data.variant?.options?.[Number(this.dataset.colourIndex || 0)];
          if (colour) this.setColour(colour);
        });
      }
    }
    cleanup() {
      this.pauseVideos(); this.zoomEvents?.abort(); this.mediaObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      if (this.rootOverflow !== undefined) document.documentElement.style.overflow = this.rootOverflow;
      this.rootOverflow = undefined;
      this.dialog?.getAnimations({subtree: true}).forEach(animation => animation.cancel());
      this.unsubscribe?.(); this.events?.abort(); this.slider?.destroy(); this.zoomSlider?.destroy(); this.thumbResize?.disconnect();
    }
    disconnectedCallback() { this.cleanup(); }
    durationLabel(milliseconds) {
      const seconds = Math.round(Number(milliseconds) / 1000);
      return seconds > 0 ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : '';
    }
    bindVideo(figure, signal) {
      const options = {signal};
      figure.querySelector('[data-yv-duration]').textContent = this.durationLabel(figure.dataset.duration);
      figure.querySelector('[data-yv-play]').addEventListener('click', () => this.playVideo(figure), options);
      figure.querySelector('[data-yv-retry]').addEventListener('click', () => {
        figure.querySelector('[data-yv-player-host]').replaceChildren();
        this.playVideo(figure);
      }, options);
      figure.querySelector('[data-yv-expand]')?.addEventListener('click', () => this.openZoom(), options);
    }
    pauseFigure(figure) {
      figure?.querySelector('video')?.pause();
      if (figure?.querySelector('iframe')) {
        figure.querySelector('[data-yv-player-host]').replaceChildren();
        delete figure.dataset.playerReady;
        figure.querySelector('[data-yv-play]').hidden = false;
      }
    }
    pauseVideos(except) {
      this.querySelectorAll('[data-yv-video]').forEach(figure => {
        if (figure !== except) this.pauseFigure(figure);
      });
    }
    mountVideo(figure) {
      const host = figure.querySelector('[data-yv-player-host]');
      if (!host.firstElementChild) {
        host.append(figure.querySelector('template').content.cloneNode(true));
        const player = host.firstElementChild;
        const status = figure.querySelector('[data-yv-video-status]');
        const signal = figure.closest('dialog') ? this.zoomEvents.signal : this.events.signal;
        const options = {signal};
        const failed = () => {
          if (!figure.isConnected || host.firstElementChild !== player) return;
          if (player.tagName === 'VIDEO') player.pause();
          delete figure.dataset.playerReady;
          figure.querySelector('[data-yv-play]').hidden = false;
          figure.querySelector('[data-yv-video-error]').hidden = false;
          status.textContent = 'Video unavailable. Try again.';
        };
        player.addEventListener('error', failed, options);
        if (player.tagName === 'VIDEO') {
          player.removeAttribute('loading');
          player.setAttribute('aria-label', figure.dataset.label);
          let loadingTimer;
          const stopWaiting = () => clearTimeout(loadingTimer);
          player.addEventListener('play', () => {
            this.pauseVideos(figure);
            stopWaiting();
            if (player.readyState < 2) loadingTimer = setTimeout(() => {
              if (!player.paused && player.readyState < 2) failed();
            }, 12000);
          }, options);
          // A failed <source> doesn't bubble a normal video error in every browser.
          const failedSources = new Set();
          player.querySelectorAll('source').forEach(source => source.addEventListener('error', () => {
            failedSources.add(source);
            if ([...player.querySelectorAll('source')].every(s => failedSources.has(s) || !player.canPlayType(s.type))) failed();
          }, options));
          ['loadedmetadata', 'playing', 'pause', 'error'].forEach(type => player.addEventListener(type, stopWaiting, options));
          signal.addEventListener('abort', stopWaiting, {once:true});
          player.addEventListener('waiting', () => { status.textContent = 'Loading film…'; figure.dataset.buffering = 'true'; }, options);
          ['playing', 'pause', 'ended', 'error'].forEach(event => player.addEventListener(event, () => {
            delete figure.dataset.buffering;
            if (event !== 'error') status.textContent = '';
          }, options));
        }
      }
      figure.dataset.playerReady = 'true';
      figure.querySelector('[data-yv-play]').hidden = true;
      figure.querySelector('[data-yv-video-error]').hidden = true;
      return host.firstElementChild;
    }
    playVideo(figure) {
      this.pauseVideos(figure);
      const player = this.mountVideo(figure);
      if (player.tagName === 'VIDEO') {
        if (player.ended) player.currentTime = 0;
        if (player.networkState === HTMLMediaElement.NETWORK_EMPTY) player.load();
        player.play().catch(() => {
          // Keep native controls available when a browser declines the play request.
          if (figure.isConnected && !player.error) figure.querySelector('[data-yv-video-status]').textContent = 'Use the play control to start the film.';
        });
        player.focus({preventScroll: true});
      } else player.focus({preventScroll: true});
    }
    normalise(value) { return String(value).toLowerCase().split('(')[0].replace(/[^a-z0-9]/g, ''); }
    setColour(value) {
      const colour = this.normalise(value);
      if (colour === this.currentColour) return;
      this.currentColour = colour; this.dataset.colour = value;
      this.pauseVideos();
      if (this.dialog.open) this.dialog.close();
      this.visiblePhotos = this.photos.filter(photo => photo.hasAttribute('data-shared') || this.normalise(photo.dataset.colour) === colour);
      this.photos.forEach((photo, index) => {
        photo.hidden = !this.visiblePhotos.includes(photo);
        photo.dataset.active = 'false';
        this.thumbnails[index].hidden = photo.hidden;
      });
      this.slider.reset(this.visiblePhotos);
      this.thumbnailRail.scrollTo({left: 0, behavior: 'instant'});
    }
    move(direction) { this.slider.move(direction); }
    show(index, smooth = true) { if (index >= 0) this.slider.go(index, smooth); }
    update(index) {
      const active = this.visiblePhotos[index];
      if (!active) return;
      this.activeIndex = index;
      this.pauseVideos(this.dialog.open ? this.querySelector('[data-yv-zoom-track]')?.children[index] : active);
      this.thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(this.photos[i] === active)));
      window.YVFadeCaption(this.querySelector('[data-yv-caption]'), active.dataset.label);
      this.querySelector('[data-yv-count]').textContent = `${index + 1} / ${this.visiblePhotos.length}`;
      this.previous.disabled = index === 0; this.next.disabled = index === this.visiblePhotos.length - 1;
      window.YVPhotoDots(this.querySelector('[data-yv-product-dots]'), this.visiblePhotos.length, index, i => this.show(i));
      [...this.querySelector('[data-yv-product-dots]').children].forEach((button, i) => {
        button.setAttribute('aria-label', `Show ${this.visiblePhotos[i].hasAttribute('data-yv-video') ? 'video' : 'photo'} ${i + 1}`);
      });
      this.positionThumbnail();
      const button = this.thumbnails[this.photos.indexOf(active)];
      this.thumbnailRail.scrollTo({left: button.offsetLeft - (this.thumbnailRail.clientWidth - button.offsetWidth) / 2,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      if (this.dialog.open && this.zoomSlider && this.zoomSlider.index !== index) this.zoomSlider.go(index, false);
    }
    positionThumbnail() {
      const photo = this.visiblePhotos?.[this.activeIndex];
      const button = this.thumbnails?.[this.photos.indexOf(photo)];
      if (!button || !this.thumbIndicator) return;
      this.thumbIndicator.style.width = `${button.offsetWidth}px`;
      this.thumbIndicator.style.height = `${button.offsetHeight}px`;
      this.thumbIndicator.style.transform = `translate(${button.offsetLeft}px, ${button.offsetTop}px)`;
    }
    async closeZoom() {
      if (!this.dialog.open || this.closing) return;
      const expandedPlayer = this.querySelector('[data-yv-zoom-track]')?.children[this.activeIndex]?.querySelector('video');
      const inlinePlayer = this.visiblePhotos[this.activeIndex]?.querySelector('video');
      if (expandedPlayer && inlinePlayer && inlinePlayer.readyState > 0) inlinePlayer.currentTime = expandedPlayer.currentTime;
      this.pauseVideos();
      this.closing = true;
      this.dialog.getAnimations().forEach(animation => animation.cancel());
      const source = this.slider.rail.getBoundingClientRect(), target = this.dialog.getBoundingClientRect();
      const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 360;
      const transform = `translate(${source.left - target.left}px, ${source.top - target.top}px) scale(${source.width / target.width}, ${source.height / target.height})`;
      const animation = this.dialog.animate([{transform:'none', opacity:1}, {transform, opacity:.2}],
        {duration, easing:'cubic-bezier(.4,0,.2,1)'});
      await animation.finished.catch(() => {});
      this.dialog.close();
    }
    async openZoom() {
      if (this.dialog.open || this.opening) return;
      this.opening = true;
      const selectedPhoto = this.visiblePhotos[this.activeIndex];
      const inlinePlayer = selectedPhoto.querySelector('video');
      const playback = inlinePlayer ? {time: inlinePlayer.currentTime, playing: !inlinePlayer.paused, muted: inlinePlayer.muted, volume: inlinePlayer.volume} : null;
      this.pauseVideos(); this.zoomEvents?.abort(); this.zoomEvents = new AbortController();
      this.zoomSlider?.destroy();
      const rail = this.querySelector('[data-yv-zoom-track]');
      const images = this.visiblePhotos.map(photo => {
        if (photo.hasAttribute('data-yv-video')) {
          const frame = photo.cloneNode(true);
          frame.hidden = false;
          frame.removeAttribute('data-yv-photo');
          frame.removeAttribute('inert'); frame.removeAttribute('aria-hidden');
          frame.removeAttribute('style');
          delete frame.dataset.playerReady; delete frame.dataset.buffering;
          frame.querySelector('[data-yv-player-host]').replaceChildren();
          frame.querySelector('[data-yv-play]').hidden = false;
          frame.querySelector('[data-yv-video-error]').hidden = true;
          frame.querySelector('[data-yv-expand]').remove();
          frame.querySelector('noscript')?.remove();
          // bindVideo also supports frames without the redundant enlarged-view control.
          this.bindVideo(frame, this.zoomEvents.signal);
          return frame;
        }
        const original = photo.querySelector('img'), image = document.createElement('img');
        image.src = original.dataset.zoom || original.currentSrc || original.src;
        image.alt = original.alt; image.loading = 'lazy'; image.draggable = false;
        image.width = original.width; image.height = original.height;
        return image;
      });
      const selectedImage = images[this.activeIndex];
      if (selectedImage.tagName === 'IMG') {
        selectedImage.loading = 'eager';
        try { await selectedImage.decode(); } catch {
          selectedImage.src = selectedPhoto.querySelector('img').currentSrc || selectedPhoto.querySelector('img').src;
        }
      }
      this.opening = false;
      if (!this.isConnected || this.visiblePhotos[this.activeIndex] !== selectedPhoto) return;
      rail.replaceChildren(...images);
      const origin = this.slider.rail.getBoundingClientRect();
      this.rootOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.zoomSlider = new window.YVSwipeRail(rail, {items: images, initialIndex: this.activeIndex, onChange: index => {
        this.pauseVideos(images[index]);
        this.querySelector('[data-yv-zoom-count]').textContent = `${index + 1} / ${images.length}`;
        window.YVPhotoDots(this.querySelector('[data-yv-zoom-dots]'), images.length, index, i => this.zoomSlider.go(i));
        this.querySelector('[data-yv-zoom-previous]').disabled = index === 0;
        this.querySelector('[data-yv-zoom-next]').disabled = index === images.length - 1;
        if (index !== this.activeIndex) this.show(index, false);
      }});
      this.zoomSlider.go(this.activeIndex, false);
      if (playback && selectedImage.hasAttribute('data-yv-video')) {
        const player = this.mountVideo(selectedImage);
        player.muted = playback.muted; player.volume = playback.volume;
        player.addEventListener('loadedmetadata', () => { player.currentTime = playback.time; }, {once: true, signal: this.zoomEvents.signal});
        player.preload = 'metadata';
        player.load();
        if (playback.playing) player.play().catch(() => {});
      }
      const target = this.dialog.getBoundingClientRect();
      const transform = `translate(${origin.left - target.left}px, ${origin.top - target.top}px) scale(${origin.width / target.width}, ${origin.height / target.height})`;
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) this.dialog.animate(
        [{transform, opacity:.4}, {transform:'none', opacity:1}],
        {duration:650, easing:'cubic-bezier(.22,1,.36,1)'});
      this.querySelector('[data-yv-close]').focus({preventScroll:true});
    }
  });
}
